import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {
	FetchBasePriceForFerryTrips,
	BasePriceFerryTripsDto,
} from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { dataToFerryTripBookingDto } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';

export function useTripPricing(
	departureTickets: TicketsTabTrip[] | null,
	returningTickets: TicketsTabTrip[] | null,
	departureTripsLoading: boolean,
	destinationTripsLoading: boolean,
	wizardData: BookingWizardData,
	passengerTicketDependencies: any[],
	/**
	 * Trip id of the existing alteration leg (when applicable). When set, the hook fires an
	 * immediate non-debounced price fetch for just that trip on mount so the trip card paints
	 * with a price while the multi-day window price fetch runs in the background.
	 */
	initialDepartureTripId?: string | null,
	initialReturnTripId?: string | null,
) {
	// Two parallel maps: prices for the full window (debounced fetch, possibly null while
	// loading) and prices for the alteration's existing trip (immediate fetch). Exposed price
	// map prefers window when present, falls back to single-trip otherwise.
	const [windowDeparturePrices, setWindowDeparturePrices] = useState<{ [key: string]: number } | null>(null);
	const [windowReturnPrices, setWindowReturnPrices] = useState<{ [key: string]: number } | null>(null);
	const [singleDeparturePrices, setSingleDeparturePrices] = useState<{ [key: string]: number } | null>(null);
	const [singleReturnPrices, setSingleReturnPrices] = useState<{ [key: string]: number } | null>(null);

	// Store wizardData in ref to prevent fetchPrices from recreating on every wizardData change
	// This breaks the cascade: wizardData change -> fetchPrices recreate -> debounced recreate -> useEffect re-run
	const wizardDataRef = useRef(wizardData);
	wizardDataRef.current = wizardData;

	// Refs so a stale single-trip response can't overwrite the result of a newer alteration
	// (i.e. user cancels the alteration and starts another mid-flight).
	const initialDepartureTripIdRef = useRef(initialDepartureTripId);
	initialDepartureTripIdRef.current = initialDepartureTripId;
	const initialReturnTripIdRef = useRef(initialReturnTripId);
	initialReturnTripIdRef.current = initialReturnTripId;

	const fetchPrices = useCallback(
		async (tickets: TicketsTabTrip[], isDeparture: boolean): Promise<{ [key: string]: number }> => {
			return FetchBasePriceForFerryTrips({
				inCheckIn: false,
				bookingCreationDto: dataToFerryTripBookingDto(wizardDataRef.current, false, false),
				ferryTripIds: tickets.map(x => x.id),
				journeys: tickets.map(x => ({ tripId: x.id, startStopId: x.startStopId, endStopId: x.endStopId })),
				departureBooking: isDeparture,
			} as BasePriceFerryTripsDto);
		},
		[],
	); // No dependencies - uses ref which is stable

	const debouncedDeparturePricing = useCallback(AwesomeDebouncePromise(fetchPrices, 500), [fetchPrices]);

	const debouncedReturnPricing = useCallback(AwesomeDebouncePromise(fetchPrices, 500), [fetchPrices]);

	// One-shot non-debounced fetch for the alteration's existing departure trip. Runs as
	// soon as the single-trip ticket lands in `departureTickets`, so the trip card paints
	// with a price before the multi-day window fetch returns. The debounced effect below
	// will then replace `windowDeparturePrices` with the full map when the window lands.
	const departureSingleHasFiredRef = useRef(false);
	useEffect(() => {
		if (!initialDepartureTripId || !departureTickets) {
			return;
		}
		const trip = departureTickets.find(t => t.id === initialDepartureTripId);
		if (!trip) {
			return;
		}
		if (departureSingleHasFiredRef.current) {
			return;
		}
		departureSingleHasFiredRef.current = true;
		const requestedId = initialDepartureTripId;
		fetchPrices([trip], true).then(prices => {
			if (initialDepartureTripIdRef.current !== requestedId) return;
			setSingleDeparturePrices(prev => ({ ...prev, ...prices }));
		});
	}, [initialDepartureTripId, departureTickets, fetchPrices]);

	// Same single-trip fast path for the return leg.
	const returnSingleHasFiredRef = useRef(false);
	useEffect(() => {
		if (!initialReturnTripId || !returningTickets) {
			return;
		}
		const trip = returningTickets.find(t => t.id === initialReturnTripId);
		if (!trip) {
			return;
		}
		if (returnSingleHasFiredRef.current) {
			return;
		}
		returnSingleHasFiredRef.current = true;
		const requestedId = initialReturnTripId;
		fetchPrices([trip], false).then(prices => {
			if (initialReturnTripIdRef.current !== requestedId) return;
			setSingleReturnPrices(prev => ({ ...prev, ...prices }));
		});
	}, [initialReturnTripId, returningTickets, fetchPrices]);

	// Fetch departure prices (debounced, full window)
	useEffect(() => {
		if (departureTripsLoading || wizardData.tabSelected === 'freight') {
			return;
		}

		if (departureTickets && departureTickets.length > 0) {
			debouncedDeparturePricing(departureTickets, true).then(prices => {
				setWindowDeparturePrices(prices);
			});
		} else {
			setWindowDeparturePrices({});
		}
	}, [
		departureTickets,
		departureTripsLoading,
		wizardData.tabSelected,
		debouncedDeparturePricing,
		...passengerTicketDependencies,
	]);

	// Fetch return prices (debounced, full window)
	useEffect(() => {
		if (destinationTripsLoading || wizardData.tabSelected === 'freight') {
			return;
		}

		if (returningTickets && returningTickets.length > 0 && wizardData.tripType === 'return') {
			debouncedReturnPricing(returningTickets, false).then(prices => {
				setWindowReturnPrices(prices);
			});
		} else {
			setWindowReturnPrices({});
		}
	}, [
		returningTickets,
		destinationTripsLoading,
		wizardData.tabSelected,
		wizardData.tripType,
		debouncedReturnPricing,
		...passengerTicketDependencies,
	]);

	// Merged maps surfaced to consumers. Window takes precedence (it's the freshest +
	// includes the single trip); single-trip data fills in while window is in flight.
	const priceByDepartureFerryTrip = useMemo(
		() => ({
			...(singleDeparturePrices ?? {}),
			...(windowDeparturePrices ?? {}),
		}),
		[singleDeparturePrices, windowDeparturePrices],
	);

	const priceByReturnFerryTrip = useMemo(
		() => ({
			...(singleReturnPrices ?? {}),
			...(windowReturnPrices ?? {}),
		}),
		[singleReturnPrices, windowReturnPrices],
	);

	// "Loading" = we have no prices at all yet. Once the single-trip price arrives the card
	// can render even though the window fetch is still in flight.
	const departurePriceLoading = windowDeparturePrices == null && singleDeparturePrices == null;
	const destinationPriceLoading = windowReturnPrices == null && singleReturnPrices == null;

	return {
		priceByDepartureFerryTrip,
		priceByReturnFerryTrip,
		departurePriceLoading,
		destinationPriceLoading,
	};
}
